/**
 * /platform/calculators — SEO landing page for the Household + supporting calculators.
 */
import React from 'react';
import {
  Home, ChefHat, DollarSign, Zap, Percent, ChefHat as CHat,
  Package, Timer, TrendingUp, Sparkles, Users, BookOpen, Bot,
  Calculator, Rocket,
} from 'lucide-react';
import PlatformFeaturePage from './PlatformFeaturePage';

const CalculatorsPage = () => (
  <PlatformFeaturePage
    slug="calculators"
    eyebrow="Calculators"
    title="Household calculators for"
    titleAccent="hobby freeze-drying done right."
    subtitle="Cost-per-run, energy math, moisture yield and shelf-life estimates — the everyday numbers hobbyists actually need before hitting Start."
    seo={{
      title: 'Freeze Dry Household Calculator — Cost, Energy, Yield',
      description: 'Free household freeze-dry calculators: batch cost per run, energy per batch, yield estimate, moisture and shelf life.',
      keywords: 'freeze dry cost calculator, freeze dryer household calculator, freeze drying energy calculator, yield calculator',
    }}
    highlights={[
      { icon: DollarSign, label: 'Cost per run' },
      { icon: Zap,        label: 'Energy math' },
      { icon: Percent,    label: 'Yield %' },
      { icon: Timer,      label: 'Shelf life' },
    ]}
    primaryCta={{ label: 'Open Household Calculator', to: '/login?mode=signup' }}
    secondaryCta={{ label: 'Running a business? Try Business Tools', to: '/platform/business-tools' }}
    howItWorks={[
      { title: 'Add your food items',     body: 'Weight, cost per pound, and expected water content. Add as many items as you want — meat + veggies + dairy all in one batch.' },
      { title: 'Set your machine defaults',body: 'Pick your freeze dryer size (or set custom kWh and cycle hours) and your local electricity price. You do this once — it remembers next time.' },
      { title: 'Read the outputs',        body: 'Cost per run, kWh consumed, expected yield in pounds, estimated shelf life. Save the batch to your history for later.' },
    ]}
    features={[
      { icon: Package,    title: 'Multi-item batches',      body: 'Real batches aren\'t one thing — mix beef + strawberries + cheese and the calculator handles the moisture math per item.' },
      { icon: Zap,        title: 'Energy per batch',        body: 'kWh × local rate × cycle length = the actual electricity cost for this specific batch. No more guessing "about $2 in power".' },
      { icon: Percent,    title: 'Yield estimator',         body: 'Predicts finished pounds and % moisture removal so you know what to expect *before* opening the door.' },
      { icon: Timer,      title: 'Shelf life',              body: 'Estimate storage life in Mylar with O₂ absorbers based on the food type and finished moisture — with a note when it\'s outside the safe range.' },
      { icon: TrendingUp, title: 'Batch history',           body: 'Every calculation saves to your account so you can compare month-over-month or check what a similar past batch cost.' },
      { icon: Sparkles,   title: 'AI batch analysis',       body: 'One-click recommendation on how to lower cost per pound, extend shelf life, or improve yield next time.' },
    ]}
    personas={[
      { icon: CHat,   title: 'Home preppers',              body: 'Building a long-term pantry? Track cost-per-pound-shelf-stable across every batch to hit your budget.' },
      { icon: ChefHat,title: 'Weekend batchers',           body: 'You freeze-dry Sunday afternoons. This tells you if that batch of cheddar was actually cheaper than store-bought.' },
      { icon: Rocket, title: 'Aspiring pros',              body: 'Test batches before you go commercial — this is where your product economics start.' },
    ]}
    faqs={[
      { q: 'What\'s the difference between the Household and Business calculator?',
        a: 'Household focuses on one batch at a time — cost, energy, yield and shelf life. Business models a whole operation — margins, capacity, break-even, sensitivity and a printable pro-forma. Most operators start with Household and graduate to Business when they sell their first bag.' },
      { q: 'Do I need my exact electricity rate?',
        a: 'It defaults to the US average and pulls from your city (if you set one). Enter your utility rate for accuracy — even a rough estimate lands you within 10% of actual.' },
      { q: 'How accurate is the shelf-life estimate?',
        a: 'It\'s a research-backed *range* based on food category, finished moisture, and storage method — not a food-safety guarantee. Always err on the shorter end for high-oil foods.' },
      { q: 'Can I export a shopping list?',
        a: 'Yes — the calculator can print a per-batch shopping list with your quantities, ready to take to your favourite place to purchase goods.' },
    ]}
    related={[
      { slug: 'business-tools', title: 'Business Tools',   icon: Calculator, blurb: 'When Household stops being enough.' },
      { slug: 'ai-assistant',   title: 'AI Assistant',     icon: Bot,        blurb: 'Ask "why was yield low on this batch?"' },
      { slug: 'learning',       title: 'Learning & Courses', icon: BookOpen, blurb: 'The physics behind each number.' },
    ]}
    finalCtaTitle="Know your cost per pound before you push Start."
    finalCtaBody="Sign up free — the Household Calculator is included on every tier."
  />
);

export default CalculatorsPage;
